import { useEffect } from "react";
import { useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import { useEvents } from "@/store/events";
import { useOutbox } from "@/store/outbox";
import { useMediaDownloads } from "@/store/mediaDownloads";
import { useToast } from "@/components/ui/toast";
import type { Account, AccountUnread, Chat, Message, MessageDirection, WSEvent } from "@/lib/types";

/**
 * Правит кэш react-query прямо по WS-событию вместо `invalidateQueries`.
 *
 * Раньше каждое входящее сообщение перезапрашивало и список чатов аккаунта
 * (до 100 строк), и всю историю открытого чата — а история это infinite query,
 * то есть refetch тянул ВСЕ загруженные страницы подряд. При живой переписке на
 * десятке аккаунтов это давало постоянный поток запросов и заметные подвисания.
 *
 * События воркера несут полное тело сообщения, поэтому список и переписка
 * обновляются локально и мгновенно. Через `invalidateQueries` идут только
 * структурные события (синк диалогов, массовые операции), где точечной правки
 * недостаточно.
 */

interface MessageEventData {
  message_id?: string | null;
  chat_id?: string;
  tg_message_id?: number;
  direction?: MessageDirection;
  text?: string | null;
  service?: string | null;
  note?: string | null;
  media_id?: string | null;
  media_group_id?: number | null;
  media?: {
    id: string;
    kind: string;
    mime?: string | null;
    size?: number;
    original_name?: string | null;
    downloaded?: boolean;
  } | null;
  reply_to_tg_id?: number | null;
  sender_tg_id?: number | null;
  sent_at?: string;
  command_id?: string;
  buttons?: Message["buttons"];
}

function toMessage(chatId: string, d: MessageEventData, fallbackDirection: MessageDirection): Message | null {
  if (d.tg_message_id == null) return null;
  return {
    id: d.message_id ?? `ws:${chatId}:${d.tg_message_id}`,
    chat_id: chatId,
    tg_message_id: d.tg_message_id,
    direction: d.direction ?? fallbackDirection,
    sender_tg_id: d.sender_tg_id ?? null,
    text: d.text ?? null,
    media_id: d.media_id ?? null,
    media_group_id: d.media_group_id ?? null,
    media: d.media
      ? {
          id: d.media.id,
          kind: d.media.kind,
          mime: d.media.mime ?? null,
          size: d.media.size ?? 0,
          original_name: d.media.original_name ?? null,
          // Наши исходящие события этого поля не несут — их файл и так у нас.
          downloaded: d.media.downloaded ?? true,
        }
      : null,
    reply_to_tg_id: d.reply_to_tg_id ?? null,
    sent_at: d.sent_at ?? new Date().toISOString(),
    deleted_at: null,
    buttons: d.buttons ?? null,
    service: d.service ?? null,
    note: d.note ?? null,
  };
}

/** Вставляет или заменяет сообщение во всех страницах истории чата. */
function upsertMessage(qc: QueryClient, accountId: string, chatId: string, msg: Message) {
  qc.setQueriesData<InfiniteData<Message[]>>(
    { queryKey: ["messages", accountId, chatId] },
    (old) => {
      if (!old) return old;
      const known = old.pages.some((p) => p.some((m) => m.tg_message_id === msg.tg_message_id));
      if (known) {
        return {
          ...old,
          pages: old.pages.map((p) =>
            p.map((m) => (m.tg_message_id === msg.tg_message_id ? { ...m, ...msg } : m))
          ),
        };
      }
      // Страницы приходят newest-first, свежее сообщение — в начало первой.
      const [first = [], ...rest] = old.pages;
      return { ...old, pages: [[msg, ...first], ...rest] };
    }
  );
}

function patchMessage(
  qc: QueryClient,
  accountId: string,
  chatId: string,
  tgMessageId: number,
  patch: Partial<Message>
) {
  qc.setQueriesData<InfiniteData<Message[]>>(
    { queryKey: ["messages", accountId, chatId] },
    (old) =>
      old
        ? {
            ...old,
            pages: old.pages.map((p) =>
              p.map((m) => (m.tg_message_id === tgMessageId ? { ...m, ...patch } : m))
            ),
          }
        : old
  );
}

function patchChat(
  qc: QueryClient,
  accountId: string,
  chatId: string,
  patch: (c: Chat) => Chat,
  { resort = false }: { resort?: boolean } = {}
) {
  qc.setQueryData<Chat[]>(["chats", accountId], (old) => {
    if (!old) return old;
    const next = old.map((c) => (c.id === chatId ? patch(c) : c));
    if (!resort) return next;
    return [...next].sort((a, b) => {
      const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return tb - ta;
    });
  });
}

/** Сдвигает счётчик непрочитанного аккаунта, не ходя в сеть. */
function bumpAccountUnread(qc: QueryClient, accountId: string, newChat: boolean) {
  qc.setQueryData<AccountUnread[]>(["accounts", "unread"], (old) => {
    if (!old) return old;
    const known = old.some((u) => u.account_id === accountId);
    if (!known) {
      return [...old, { account_id: accountId, unread_chats: 1, unread_messages: 1 }];
    }
    return old.map((u) =>
      u.account_id === accountId
        ? {
            ...u,
            unread_chats: u.unread_chats + (newChat ? 1 : 0),
            unread_messages: u.unread_messages + 1,
          }
        : u
    );
  });
}

export function useLiveCache(): void {
  const qc = useQueryClient();
  const { push } = useToast();

  useEffect(() => {
    return useEvents.getState().subscribe("*", (e: WSEvent) => {
      // Единственное событие без account_id (прокси может быть общим на
      // несколько аккаунтов) — обрабатываем отдельно, до guard'а ниже.
      if (e.type === "proxy.unhealthy") {
        const dd = e.data as { host?: string; port?: number; error?: string };
        qc.invalidateQueries({ queryKey: ["proxies"] });
        qc.invalidateQueries({ queryKey: ["proxy-pool"] });
        qc.invalidateQueries({ queryKey: ["accounts"] });
        push({
          title: "Прокси перестал работать",
          description: `${dd.host ?? "?"}:${dd.port ?? "?"}${dd.error ? " — " + dd.error : ""}`,
          variant: "destructive",
          durationMs: 8000,
        });
        return;
      }

      const accountId = e.account_id;
      if (!accountId) return;
      const d = e.data as MessageEventData;
      const chatId = d.chat_id;

      switch (e.type) {
        // Первое сообщение от ранее неизвестного собеседника: строки чата
        // ещё нет в кэше, поэтому patchChat ниже (в message.new/outgoing)
        // её не найдёт — вставляем явно, до применения патча с текстом.
        case "chat.new": {
          const dd = e.data as Partial<Chat> & { id?: string };
          if (!dd.id) break;
          qc.setQueryData<Chat[]>(["chats", accountId], (old) => {
            if (!old) return old;
            if (old.some((c) => c.id === dd.id)) return old;
            const chat: Chat = {
              id: dd.id!,
              account_id: dd.account_id ?? accountId,
              tg_peer_id: dd.tg_peer_id ?? 0,
              kind: dd.kind ?? "private",
              title: dd.title ?? null,
              username: dd.username ?? null,
              unread_count: dd.unread_count ?? 0,
              outbox_read_max_id: dd.outbox_read_max_id ?? null,
              last_message_at: dd.last_message_at ?? null,
              last_message: null,
              history_synced_at: dd.history_synced_at ?? null,
              peer_blocked_at: dd.peer_blocked_at ?? null,
              peer_blocked_reason: dd.peer_blocked_reason ?? null,
              avatar_media_id: dd.avatar_media_id ?? null,
            };
            return [chat, ...old];
          });
          break;
        }

        case "message.new":
        case "message.outgoing": {
          if (!chatId) break;
          // Ответ воркера на нашу отправку: снимаем оптимистичный пузырь —
          // ниже его место займёт настоящее сообщение.
          if (d.command_id) useOutbox.getState().removeByCommandId(d.command_id);

          const incoming = e.type === "message.new";
          const msg = toMessage(chatId, d, incoming ? "in" : "out");
          if (msg) upsertMessage(qc, accountId, chatId, msg);

          // Был ли чат прочитанным до этого сообщения — от этого зависит,
          // прибавлять ли аккаунту ещё один непрочитанный диалог.
          const wasRead =
            (qc.getQueryData<Chat[]>(["chats", accountId]) ?? []).find((c) => c.id === chatId)
              ?.unread_count === 0;

          patchChat(
            qc,
            accountId,
            chatId,
            (c) => ({
              ...c,
              last_message_at: msg?.sent_at ?? c.last_message_at,
              last_message: msg
                ? {
                    tg_message_id: msg.tg_message_id,
                    direction: msg.direction,
                    text: msg.text || msg.note,
                    media_kind: msg.media?.kind ?? null,
                    service: msg.service,
                    sent_at: msg.sent_at,
                  }
                : c.last_message,
              // Счётчик растёт и у открытого чата: MessagePane тут же гасит
              // его и шлёт mark_read, поэтому собеседник получает отметку
              // «прочитано» и на сообщения, пришедшие при открытом диалоге.
              unread_count: incoming ? c.unread_count + 1 : c.unread_count,
            }),
            { resort: true }
          );
          // Счётчик аккаунта двигаем локально: иначе каждое входящее тянуло бы
          // запрос агрегата, ради которого всё это и затевалось. Расхождение
          // подчистит фоновый рефетч раз в минуту.
          if (incoming) bumpAccountUnread(qc, accountId, wasRead);
          break;
        }

        case "message.edited": {
          if (!chatId || d.tg_message_id == null) break;
          patchMessage(qc, accountId, chatId, d.tg_message_id, { text: d.text ?? null });
          break;
        }

        case "message.deleted": {
          if (!chatId || d.tg_message_id == null) break;
          patchMessage(qc, accountId, chatId, d.tg_message_id, {
            deleted_at: new Date().toISOString(),
          });
          break;
        }

        case "chat.read": {
          const dd = e.data as { chat_id?: string; unread_count?: number };
          if (!dd.chat_id) break;
          patchChat(qc, accountId, dd.chat_id, (c) => ({
            ...c,
            unread_count: dd.unread_count ?? 0,
          }));
          qc.invalidateQueries({ queryKey: ["accounts", "unread"] });
          break;
        }

        case "chat.read_outbox": {
          const dd = e.data as { chat_id?: string; outbox_read_max_id?: number };
          const maxId = dd.outbox_read_max_id;
          if (!dd.chat_id || maxId == null) break;
          patchChat(qc, accountId, dd.chat_id, (c) => ({
            ...c,
            outbox_read_max_id: Math.max(c.outbox_read_max_id ?? 0, maxId),
          }));
          break;
        }

        case "chat.avatar_updated": {
          const dd = e.data as { chat_id?: string; avatar_media_id?: string };
          if (!dd.chat_id || !dd.avatar_media_id) break;
          patchChat(qc, accountId, dd.chat_id, (c) => ({
            ...c,
            avatar_media_id: dd.avatar_media_id!,
          }));
          break;
        }

        case "chat.peer_blocked": {
          const dd = e.data as { chat_id?: string; reason?: string; at?: string };
          if (!dd.chat_id) break;
          patchChat(qc, accountId, dd.chat_id, (c) => ({
            ...c,
            peer_blocked_at: dd.at ?? new Date().toISOString(),
            peer_blocked_reason: dd.reason ?? null,
          }));
          break;
        }

        // Команда не доехала: пузырь остаётся, но помечается сбойным —
        // текст не теряется, отправку можно повторить.
        case "command.rejected":
        case "command.failed": {
          const dd = e.data as { command_id?: string; reason?: string; detail?: string };
          if (dd.command_id) {
            useOutbox
              .getState()
              .failByCommandId(dd.command_id, dd.detail || dd.reason || "не доставлено");
          }
          break;
        }

        // Структурные изменения: состав диалогов меняется целиком,
        // точечной правкой не обойтись.
        case "chat.started":
        case "chat.deleted":
        case "chat.left":
        case "chats.synced":
        case "chats.purged":
        case "chats.bulk_blocked":
        case "chats.bulk_deleted":
        case "chat.backfilled": {
          qc.invalidateQueries({ queryKey: ["chats", accountId] });
          qc.invalidateQueries({ queryKey: ["accounts", "unread"] });
          if (e.type === "chat.backfilled" && chatId) {
            qc.invalidateQueries({ queryKey: ["messages", accountId, chatId] });
          }
          // Первый синк проставил аккаунту dialogs_synced_at — список чатов
          // перестаёт показывать «загружаем». Дальше синк идёт раз в пять минут
          // на каждом аккаунте, и перезапрашивать на каждый весь список
          // аккаунтов незачем.
          if (
            e.type === "chats.synced" &&
            qc.getQueryData<Account[]>(["accounts"])?.some((a) => a.id === accountId && !a.dialogs_synced_at)
          ) {
            qc.invalidateQueries({ queryKey: ["accounts"], exact: true });
          }
          break;
        }

        case "media.progress": {
          const dd = e.data as { media_id?: string; received?: number; total?: number };
          if (dd.media_id) {
            useMediaDownloads.getState().progress(dd.media_id, dd.received ?? 0, dd.total ?? 0);
          }
          break;
        }

        case "media.download_failed": {
          const dd = e.data as { media_id?: string; reason?: string };
          if (dd.media_id) {
            useMediaDownloads.getState().fail(dd.media_id, dd.reason || "не удалось скачать");
          }
          break;
        }

        // Файл лёг на диск: помечаем вложение скачанным во всех загруженных
        // страницах чата — пузырь сам переключится с карточки на превью.
        case "media.downloaded": {
          const dd = e.data as { media_id?: string; chat_id?: string; size?: number };
          if (!dd.media_id || !dd.chat_id) break;
          qc.setQueriesData<InfiniteData<Message[]>>(
            { queryKey: ["messages", accountId, dd.chat_id] },
            (old) =>
              old
                ? {
                    ...old,
                    pages: old.pages.map((p) =>
                      p.map((m) =>
                        m.media?.id === dd.media_id
                          ? { ...m, media: { ...m.media!, downloaded: true, size: dd.size ?? m.media!.size } }
                          : m
                      )
                    ),
                  }
                : old
          );
          useMediaDownloads.getState().done(dd.media_id);
          break;
        }

        case "account.no_proxy": {
          qc.invalidateQueries({ queryKey: ["accounts"] });
          push({
            title: "Аккаунт остался без прокси",
            description: String((e.data as { reason?: string })?.reason ?? ""),
            variant: "destructive",
          });
          break;
        }

        case "account.status":
        case "account.flood":
        case "account.frozen":
        case "profile.updated":
        case "profile.photo_updated":
        case "profile.photo_deleted": {
          qc.invalidateQueries({ queryKey: ["accounts"] });
          break;
        }
      }
    });
  }, [qc, push]);
}
